import { useNavigationTableSort } from "components/scrapEyeDataDashboard/navigation/useNavigationTableSort";
import { choiceQuery } from "components/scrapEyeDataDashboard/navigation/queryCodecs";
import { useNavigationQuery } from "components/scrapEyeDataDashboard/navigation/NavigationQueryProvider";
import {
  Alert,
  LinearProgress,
  Button,
  Card,
  CardContent,
  CardHeader,
  MenuItem,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
} from "@mui/material";
import { SortableHeaderCell } from "components/common/SortableHeaderCell";
import dayjs from "dayjs";
import { fetchErrorDetail } from "helpers";
import { useTenantTranslation } from "hooks/formatters";
import { useTenant } from "hooks/settings";
import { SortValueGetters } from "hooks/useTableSort";
import { useState } from "react";
import {
  ScrapEyeSensorPlacementRead,
  useInstallSensorMutation,
  useCorrectSensorInstallationMutation,
  useListRecordingPositionsQuery,
  useListSensorInstallationsQuery,
  useListSensorsQuery,
} from "src/store/api/generatedApi";
import { InstallationViewToggle } from "./InstallationViewToggle";
import { formatInstallationTimestamp } from "./installationTimestamp";
import { SensorEditorDialog } from "./SensorEditorDialog";
import { SensorInterval, SensorIntervalFields } from "./SensorIntervalFields";

interface InstallationEditorProps {
  installation: ScrapEyeSensorPlacementRead | null;
  onClose: () => void;
}

export const InstallationEditor = ({
  installation,
  onClose,
}: InstallationEditorProps) => {
  const tenantName = useTenant();
  const { t } = useTenantTranslation();
  const { data: sensors } = useListSensorsQuery({ tenantName });
  const { data: positions } = useListRecordingPositionsQuery({ tenantName });
  const [interval, setInterval] = useState<SensorInterval>(() => ({
    sensorId: installation?.sensor_id ?? null,
    startedAt: installation?.installed_at ?? dayjs().toISOString(),
    endedAt: installation?.removed_at ?? null,
  }));
  const [positionId, setPositionId] = useState<number | null>(
    installation?.recording_position_id ?? null
  );
  const [priority, setPriority] = useState(String(installation?.priority ?? 1));
  const [validInterval, setValidInterval] = useState(false);
  const [install, { isLoading: isCreating }] = useInstallSensorMutation();
  const [correct, { isLoading: isCorrecting }] =
    useCorrectSensorInstallationMutation();
  const busy = isCreating || isCorrecting;
  const validPriority =
    priority.trim() !== "" && Number.isInteger(Number(priority));

  const save = async () => {
    if (
      interval.sensorId == null ||
      positionId == null ||
      !validInterval ||
      !validPriority
    )
      return;
    const request = {
      tenantName,
      scrapEyeSensorPlacementCreate: {
        sensor_id: interval.sensorId,
        recording_position_id: positionId,
        priority: Number(priority),
        installed_at: interval.startedAt,
        removed_at: interval.endedAt,
      },
    };
    if (installation == null) await install(request).unwrap();
    else await correct({ ...request, versionId: installation.id }).unwrap();
  };

  return (
    <SensorEditorDialog
      title={t(installation == null ? "sensorInstallButton" : "edit")}
      busy={busy}
      valid={validInterval && positionId != null && validPriority}
      onClose={onClose}
      onSave={save}
      errorMessage={t("sensorInstallError")}
    >
      <SensorIntervalFields
        sensors={sensors?.items ?? []}
        interval={interval}
        onChange={setInterval}
        onValidityChange={setValidInterval}
        startLabel={t("installationInstalledAtLabel")}
        endLabel={t("installationRemovedAtLabel")}
        disabled={busy}
      />
      <TextField
        select
        label={t("recordingPositionNameLabel")}
        value={positionId ?? ""}
        disabled={busy}
        onChange={(event) => {
          setPositionId(Number(event.target.value));
        }}
      >
        {positions?.items.map((position) => (
          <MenuItem
            key={position.id}
            value={position.id}
          >{`${position.location_name} / ${position.name}`}</MenuItem>
        ))}
      </TextField>
      <TextField
        label={t("installationPriorityLabel")}
        type="number"
        value={priority}
        disabled={busy}
        onChange={(event) => {
          setPriority(event.target.value);
        }}
      />
    </SensorEditorDialog>
  );
};

const installationSortValueGetters: SortValueGetters<
  ScrapEyeSensorPlacementRead,
  "sensor" | "location" | "position" | "priority" | "installed" | "removed"
> = {
  sensor: (installation) => installation.sensor_name,
  location: (installation) => installation.location_name,
  position: (installation) => installation.recording_position_name,
  priority: (installation) => installation.priority,
  installed: (installation) => dayjs(installation.installed_at).valueOf(),
  removed: (installation) =>
    installation.removed_at == null
      ? null
      : dayjs(installation.removed_at).valueOf(),
};

export const SensorInstallationsSection = () => {
  const tenantName = useTenant();
  const { t } = useTenantTranslation();
  const [view, setView] = useNavigationQuery(
    "installationRecords",
    "current",
    choiceQuery(["current", "all"] as const),
    "replace"
  );
  const [editing, setEditing] = useState<{
    installation: ScrapEyeSensorPlacementRead | null;
  } | null>(null);
  const {
    data: installations,
    isFetching,
    error,
  } = useListSensorInstallationsQuery({ tenantName, kind: view });
  const { sortedRows, sort, toggleSort } = useNavigationTableSort({
    key: "installationRecordOrder",
    rows: installations?.items,
    sortValueGetters: installationSortValueGetters,
    initialSort: null,
  });
  return (
    <Card variant="outlined">
      <CardHeader title={t("sensorInstallationsTitle")} />
      <CardContent>
        <Stack spacing={2}>
          <InstallationViewToggle value={view} onChange={setView} />
          {isFetching && <LinearProgress />}
          {error != null && (
            <Alert severity="error">
              {fetchErrorDetail(error) ?? t("sensorHistoryLoadError")}
            </Alert>
          )}
          <Table size="small">
            <TableHead>
              <TableRow>
                <SortableHeaderCell
                  column="sensor"
                  sort={sort}
                  onSort={toggleSort}
                >
                  {t("sensorNameLabel")}
                </SortableHeaderCell>
                <SortableHeaderCell
                  column="location"
                  sort={sort}
                  onSort={toggleSort}
                >
                  {t("installationLocationLabel")}
                </SortableHeaderCell>
                <SortableHeaderCell
                  column="position"
                  sort={sort}
                  onSort={toggleSort}
                >
                  {t("recordingPositionNameLabel")}
                </SortableHeaderCell>
                <SortableHeaderCell
                  column="priority"
                  sort={sort}
                  onSort={toggleSort}
                >
                  {t("installationPriorityLabel")}
                </SortableHeaderCell>
                <SortableHeaderCell
                  column="installed"
                  sort={sort}
                  onSort={toggleSort}
                >
                  {t("installationInstalledAtLabel")}
                </SortableHeaderCell>
                <SortableHeaderCell
                  column="removed"
                  sort={sort}
                  onSort={toggleSort}
                >
                  {t("installationRemovedAtLabel")}
                </SortableHeaderCell>
                <TableCell />
              </TableRow>
            </TableHead>
            <TableBody>
              {sortedRows?.map((installation) => (
                <TableRow key={installation.id}>
                  <TableCell>{installation.sensor_name}</TableCell>
                  <TableCell>{installation.location_name}</TableCell>
                  <TableCell>{installation.recording_position_name}</TableCell>
                  <TableCell>{installation.priority}</TableCell>
                  <TableCell>
                    {formatInstallationTimestamp(installation.installed_at)}
                  </TableCell>
                  <TableCell>
                    {installation.removed_at == null
                      ? null
                      : formatInstallationTimestamp(installation.removed_at)}
                  </TableCell>
                  <TableCell>
                    <Button
                      onClick={() => {
                        setEditing({ installation });
                      }}
                    >
                      {t("edit")}
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
          <Button
            onClick={() => {
              setEditing({ installation: null });
            }}
          >
            {t("sensorInstallButton")}
          </Button>
        </Stack>
      </CardContent>
      {editing != null && (
        <InstallationEditor
          installation={editing.installation}
          onClose={() => {
            setEditing(null);
          }}
        />
      )}
    </Card>
  );
};
